import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowRight, Mail, Lock } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const invite = searchParams.get("invite");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bentornato!");
      nav(invite ? `/onboarding?invite=${encodeURIComponent(invite)}` : "/home");
    } else {
      toast.error(res.error || "Login fallito");
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-b from-[#EFF2FA] via-[#F4F6FB] to-[#F0EAFA]">
      {/* Subtle decorative blobs */}
      <div className="pointer-events-none absolute -top-40 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#26B5A8]/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-40 -right-32 w-[28rem] h-[28rem] rounded-full bg-[#7E3AED]/10 blur-3xl" />
      <div className="pointer-events-none absolute top-1/4 right-1/3 w-72 h-72 rounded-full bg-[#FF9E1B]/10 blur-3xl" />

      <div className="relative z-10 min-h-screen flex flex-col items-center px-6 pt-4 pb-10">
        <div className="w-full max-w-md flex flex-col items-center">
          {/* Full logo (H mark + Hopzigo wordmark) */}
          <Link to="/" className="block anim-slide-up" data-testid="login-logo">
            <img
              src="/hopzigo-full.png"
              alt="Hopzigo"
              className="w-[80vw] sm:w-[360px] max-w-[400px] h-auto"
            />
          </Link>

          {/* Heading with extra space below */}
          <div className="text-center -mt-4 mb-14 anim-slide-up">
            <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tight text-[#1A1D29]">
              {t("auth.welcomeBack")}
            </h1>
            <p className="text-[#5A6178] mt-3 text-base">
              {t("auth.welcomeSubtitle")}
            </p>
          </div>

          <form onSubmit={onSubmit} className="w-full space-y-5 anim-slide-up">
            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5A6178] mb-2" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-[#EEF1F8] text-[#7E3AED]">
                  <Mail className="w-4 h-4" />
                </span>
                <input
                  id="email"
                  type="email"
                  className="w-full h-16 pl-16 pr-5 rounded-2xl bg-white/85 backdrop-blur-sm border border-white/60 text-[#1A1D29] placeholder-[#A0A6BD] font-medium shadow-[0_10px_28px_rgba(20,32,80,0.08)] focus:outline-none focus:ring-2 focus:ring-[#1A2A6C]/30"
                  placeholder="mario@famiglia.it"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-widest text-[#5A6178] mb-2" htmlFor="password">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 grid place-items-center rounded-full bg-[#EEF1F8] text-[#1A2A6C]">
                  <Lock className="w-4 h-4" />
                </span>
                <input
                  id="password"
                  type="password"
                  className="w-full h-16 pl-16 pr-5 rounded-2xl bg-white/85 backdrop-blur-sm border border-white/60 text-[#1A1D29] placeholder-[#A0A6BD] font-medium shadow-[0_10px_28px_rgba(20,32,80,0.08)] focus:outline-none focus:ring-2 focus:ring-[#1A2A6C]/30 tracking-widest"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                />
              </div>
              <div className="text-right mt-2">
                <Link to="/forgot-password" className="text-sm font-semibold text-[#1A2A6C] hover:underline" data-testid="login-forgot-link">
                  {t("auth.forgotPassword")}
                </Link>
              </div>
            </div>

            <button
              type="submit"
              className="w-full h-16 rounded-2xl bg-[#1A2A6C] text-white font-bold text-lg shadow-[0_10px_30px_rgba(26,42,108,0.35)] hover:bg-[#14205A] active:translate-y-px transition-all inline-flex items-center justify-center gap-2 disabled:opacity-60"
              disabled={loading}
              data-testid="login-submit-button"
            >
              {loading ? t("auth.signingIn") : (<>{t("auth.signIn")} <ArrowRight className="w-5 h-5" /></>)}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-5">
            <span className="flex-1 h-px bg-[#ECEEF5]" />
            <span className="text-xs font-bold uppercase tracking-widest text-[#8A91A8]">{t("auth.or")}</span>
            <span className="flex-1 h-px bg-[#ECEEF5]" />
          </div>

          <button
            type="button"
            onClick={() => {
              // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
              const redirectUrl = window.location.origin + "/home";
              window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
            }}
            className="w-full h-14 rounded-2xl bg-white border-2 border-[#ECEEF5] text-[#1A1D29] font-bold inline-flex items-center justify-center gap-3 hover:border-[#1A2A6C]/30 hover:bg-[#F5F6FA] transition-all"
            data-testid="login-google-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
            </svg>
            {t("auth.signInWithGoogle")}
          </button>

          <p className="mt-7 text-center text-sm text-[#5A6178]">
            {t("auth.noAccount")}{" "}
            <Link to="/register" className="font-bold text-[#1A2A6C] hover:underline" data-testid="login-register-link">
              {t("auth.register")}
            </Link>
          </p>

        </div>
      </div>
    </div>
  );
}
